import type {
  DisruptionPnrGroupRow,
  DisruptionTripView,
  ITransferBoard,
  ShuttleRunView,
} from '@/lib/disruptions/types';
import { splitTripsForGroup } from '@/lib/transport/trips';
import { providerLabel } from '@/lib/transport/providers';
import { rosterVoucherCoversGroup } from '@/lib/transport/voucher-coverage';

// Pure model for the Transport desk — the remainder-first ground-transport view.
// ONE classifier decides every PNR's lane so the goal bar, the exceptions queue
// and the "settled" strip can never disagree. It composes THREE seams:
//   · the transfer board (plan + shuttle runs + per-hotel demand + facts),
//   · the disruption trips read (CAR / on-demand rides),
//   · the roster walk (per-PNR hotel + the cases-owned `roster.transfer@v1` seat).
// The operator's goal ends the shift: every passenger arrived, travels by own
// means, or has a vehicle on the way — or the queue says exactly why not.

export type TransportLane =
  /** Reached the destination — seat arrived ∨ trip COMPLETED. Done. */
  | 'ARRIVED'
  /** Moving right now: boarded on a DEPARTED run ∨ trip IN_TRANSIT. */
  | 'EN_ROUTE'
  /** With the vendor — seat on a not-departed run ∨ trip REQUESTED/DISPATCHED
   *  (incl. an ON_DEMAND request: the vendor owns it, NEVER operator-actionable). */
  | 'WAITING'
  /** Return-leg seat whose run is not due yet — NEVER in the amber remainder. */
  | 'SCHEDULED'
  /** The queue: no-shows, failed/expired trips, unseated groups, no plan. */
  | 'NEEDS_TRANSPORT'
  /** Travels by own means: seat OWN_MEANS ∨ a declared transport outcome. */
  | 'OWN_MEANS'
  /** No ground-transport need (no confirmed hotel / offer not accepted). */
  | 'NOT_NEEDED';

export interface TransportDeskRow {
  row: DisruptionPnrGroupRow;
  lane: TransportLane;
  /** i18n KEY (namespace `transport`) for the "why am I in this lane" line —
   *  this file holds keys, never language; the rendering component calls `t()`. */
  reason: string;
  /** Interpolation values for `reason`, when it takes any. */
  reasonVars?: Record<string, string | number> | undefined;
}

export interface TransportDeskState {
  rows: TransportDeskRow[];
  /** NEEDS_TRANSPORT only — pax-weighted worst first. The amber remainder. */
  queue: TransportDeskRow[];
  counts: Record<TransportLane, { pnrs: number; pax: number }>;
  /** PNRs the goal covers (everything except NOT_NEEDED). */
  goalPnrs: number;
  goalPax: number;
  /** Non-empty universe ∧ nothing left in NEEDS_TRANSPORT. */
  allSettled: boolean;
  /**
   * Issue #662: The ground airport where transport operations happen.
   * Derived from the most common overnightStation (or origin as fallback).
   * Used to format ground times (pickups, check-ins) in the correct timezone.
   */
  groundAirportIata: string | null;
}

/** Trip statuses the vendor is still working (mirror of `liveTripFor`). */
const LIVE_TRIP: ReadonlySet<string> = new Set(['REQUESTED', 'DISPATCHED', 'IN_TRANSIT']);
/** Run states before the vehicle leaves — a seat here is still waiting. */
const RUN_NOT_DEPARTED: ReadonlySet<string> = new Set([
  'PLANNED',
  'REQUESTED',
  'VEHICLE_ASSIGNED',
  'BOARDING',
]);
/** Return-leg run states that are "not due yet" (the vehicle is not at the door). */
const RUN_NOT_DUE: ReadonlySet<string> = new Set(['PLANNED', 'REQUESTED', 'VEHICLE_ASSIGNED']);

/** A CONFIRMED hotel (or a legacy reservation with no status = booked) means an
 *  airport↔hotel journey exists. A HELD offer is not a journey yet — acceptance
 *  may still decline it. */
export function hasHotelJourney(row: DisruptionPnrGroupRow): boolean {
  return !!row.hotelReservationUrn && row.hotelReservationStatus !== 'HELD';
}

/** A stay-home (`LOCAL_RESIDENT`) group whose declaration carried an address the
 *  geocoder could place: an airport→home journey exists (ride-home vertical). A
 *  declaration without a point is NOT a journey — there is nowhere to drive to. */
export function hasHomeJourney(row: DisruptionPnrGroupRow): boolean {
  return row.passengerElection === 'LOCAL_RESIDENT' && !!row.homeDestination?.coordinates;
}

/** Does this PNR need ground transport at all? A hotel journey OR a ride home. */
export function needsTransport(row: DisruptionPnrGroupRow): boolean {
  return hasHotelJourney(row) || hasHomeJourney(row);
}

/** The run a roster seat points at (hotel + leg + seq — the seat's own identity). */
export function runOfSeat(
  board: ITransferBoard | null | undefined,
  row: DisruptionPnrGroupRow,
): ShuttleRunView | undefined {
  const seat = row.transfer;
  if (!board || !seat || seat.runSeq === undefined || !row.hotelUrn) return undefined;
  return board.runs.find(
    (r) => r.hotelUrn === row.hotelUrn && r.leg === seat.leg && r.seq === seat.runSeq,
  );
}

interface Verdict {
  lane: TransportLane;
  /** i18n key — never a sentence. */
  reason: string;
  reasonVars?: Record<string, string | number> | undefined;
}

/**
 * Classify ONE PNR. Deterministic evidence order:
 *   1. a declared outcome / an OWN_MEANS seat ends the story;
 *   2. ALREADY_HAS_TRIP — a LIVE CAR trip wins over ANY seat (the seat is retired);
 *   3. the shuttle seat (roster `transfer` + the board's run) speaks;
 *   4. terminal CAR trips (completed → arrived; failed/expired → the queue);
 *   5. the roster's own CAR transfer fact (a trip the trips read didn't return);
 *   6. no evidence: the demand decides (queue with the honest no-plan reason).
 * Between 2 and 3: the group's Uber voucher IS its transport — with the vendor, used
 * whenever the passenger wants — so a seat or a car it stands in for never queues it.
 */
export function classifyPnr(
  row: DisruptionPnrGroupRow,
  trips: readonly DisruptionTripView[],
  board: ITransferBoard | null | undefined,
): Verdict {
  // 1 — a declared outcome ends the PNR's transport story regardless of the rest.
  if (row.transportOutcome) {
    return { lane: 'OWN_MEANS', reason: 'reason.ownMeansDeclared' };
  }
  if (row.transfer?.status === 'OWN_MEANS') {
    return { lane: 'OWN_MEANS', reason: 'reason.ownMeans' };
  }

  const cars = trips.filter((t) => (t.kind ?? 'CAR') === 'CAR');

  // 2 — ALREADY_HAS_TRIP: a live CAR trip wins over any seat. Most advanced first
  //     so a group with a stale REQUESTED and a live IN_TRANSIT reads as moving.
  const liveCar =
    cars.find((t) => t.status === 'IN_TRANSIT') ??
    cars.find((t) => t.status === 'DISPATCHED') ??
    cars.find((t) => t.status === 'REQUESTED');
  if (liveCar) return carVerdict(liveCar, !!row.transfer);

  // The Uber voucher (`roster.transportVoucher@v1`) — arranged, never the amber queue.
  if (rosterVoucherCoversGroup(row)) return { lane: 'WAITING', reason: 'reason.voucher' };

  // 3 — the shuttle seat speaks (roster `transfer` + the board's matching run).
  if (row.transfer?.kind === 'SHUTTLE') {
    const seatVerdict = classifySeat(row, board);
    if (seatVerdict) return seatVerdict;
  }

  // 4 — terminal CAR trips: completed = arrived; failed/expired = the queue.
  const doneCar = cars.find((t) => t.status === 'COMPLETED');
  if (doneCar) {
    return {
      lane: 'ARRIVED',
      reason:
        doneCar.leg === 'HOTEL_TO_AIRPORT'
          ? 'reason.car.completedReturn'
          : doneCar.leg === 'AIRPORT_TO_HOME'
            ? 'reason.car.completedHome'
            : 'reason.car.completedOutbound',
    };
  }
  const failedCar = cars.find((t) => t.status === 'FAILED' || t.status === 'FAILED_COMPENSATED');
  if (failedCar && needsTransport(row)) {
    if (failedCar.dispatchMode === 'ON_DEMAND') {
      return { lane: 'NEEDS_TRANSPORT', reason: 'reason.car.onDemandFailed' };
    }
    return failedCar.lastError
      ? {
          lane: 'NEEDS_TRANSPORT',
          reason: 'reason.car.failedWithError',
          reasonVars: { error: failedCar.lastError },
        }
      : { lane: 'NEEDS_TRANSPORT', reason: 'reason.car.failed' };
  }

  // 5 — the roster's own CAR fact (a trip the disruption trips read didn't carry).
  if (row.transfer && row.transfer.kind !== 'SHUTTLE') {
    const v = rosterCarVerdict(row);
    if (v) return v;
  }

  // 6 — no movement evidence at all: the demand decides.
  //     A stay-home group first: its journey is a ride HOME, which no shuttle plan
  //     ever covers — with a point it is the operator's queue until a car is
  //     dispatched; without one there is nowhere to drive to.
  if (row.passengerElection === 'LOCAL_RESIDENT' && !hasHotelJourney(row)) {
    return hasHomeJourney(row)
      ? { lane: 'NEEDS_TRANSPORT', reason: 'reason.demand.homeNoRide' }
      : { lane: 'NOT_NEEDED', reason: 'reason.demand.stayHomeNoDestination' };
  }
  if (!needsTransport(row)) {
    return {
      lane: 'NOT_NEEDED',
      reason: row.hotelReservationStatus === 'HELD'
        ? 'reason.demand.offerNotAccepted'
        : 'reason.demand.noHotel',
    };
  }
  if (!board?.plan) {
    return { lane: 'NEEDS_TRANSPORT', reason: 'reason.demand.noPlan' };
  }
  const hotelPlanned = !!row.hotelUrn && board.plan.hotels.some((h) => h.hotelUrn === row.hotelUrn);
  return hotelPlanned
    ? { lane: 'NEEDS_TRANSPORT', reason: 'reason.demand.notSeated' }
    : { lane: 'NEEDS_TRANSPORT', reason: 'reason.demand.hotelNotCovered' };
}

/** Lane of a LIVE car trip (the ALREADY_HAS_TRIP rule — the seat is retired). */
function carVerdict(trip: DisruptionTripView, seatRetired: boolean): Verdict {
  if (trip.status === 'IN_TRANSIT') {
    return {
      lane: 'EN_ROUTE',
      reason: seatRetired ? 'reason.car.onBoardRetired' : 'reason.car.onBoard',
      reasonVars: { provider: providerLabel(trip.providerName) },
    };
  }
  if (trip.status === 'DISPATCHED') {
    return {
      lane: 'WAITING',
      reason: seatRetired ? 'reason.car.driverAssignedRetired' : 'reason.car.driverAssigned',
    };
  }
  // REQUESTED. A scheduled return ride is not due yet; an ON_DEMAND request is
  // the passenger's — with the vendor either way: never operator-actionable.
  if (trip.leg === 'HOTEL_TO_AIRPORT' && trip.scheduledAt) {
    return { lane: 'SCHEDULED', reason: 'reason.car.returnScheduled' };
  }
  if (trip.dispatchMode === 'ON_DEMAND') {
    return { lane: 'WAITING', reason: 'reason.car.onDemandRequested' };
  }
  return {
    lane: 'WAITING',
    reason: seatRetired ? 'reason.car.requestedRetired' : 'reason.car.requested',
  };
}

/** Lane of a SHUTTLE seat (`roster.transfer@v1` + the board's run). `null` = the
 *  seat is inconclusive (fall through to the car / demand evidence). */
function classifySeat(row: DisruptionPnrGroupRow, board: ITransferBoard | null | undefined): Verdict | null {
  const seat = row.transfer!;
  const run = runOfSeat(board, row);
  const seq = seat.runSeq !== undefined ? ` #${seat.runSeq}` : '';
  const vars = { seq };
  const isReturn = seat.leg === 'HOTEL_TO_AIRPORT';

  // A passenger left at the pickup is ALWAYS the operator's queue.
  if (seat.boardingStatus === 'NO_SHOW') {
    return { lane: 'NEEDS_TRANSPORT', reason: 'reason.seat.noShow', reasonVars: vars };
  }

  // Arrived: the seat's own COMPLETED, or the run arrived carrying them. A run
  // that arrived while the seat never boarded is NOT an arrival — it's a miss.
  if (seat.status === 'COMPLETED' || (run?.status === 'ARRIVED' && seat.boardingStatus !== 'ASSIGNED')) {
    return {
      lane: 'ARRIVED',
      reason: isReturn ? 'reason.seat.arrivedReturn' : 'reason.seat.arrivedHotel',
      reasonVars: vars,
    };
  }
  if (run?.status === 'ARRIVED') {
    return { lane: 'NEEDS_TRANSPORT', reason: 'reason.seat.arrivedNoBoarding', reasonVars: vars };
  }

  if (seat.boardingStatus === 'BOARDED') {
    return run?.status === 'DEPARTED' || seat.status === 'IN_TRANSIT'
      ? { lane: 'EN_ROUTE', reason: 'reason.seat.departed', reasonVars: vars }
      : { lane: 'WAITING', reason: 'reason.seat.boardedAtStand', reasonVars: vars };
  }

  // Dead runs put the seat back on the operator's desk.
  if (run?.status === 'FAILED') {
    return { lane: 'NEEDS_TRANSPORT', reason: 'reason.seat.runFailed', reasonVars: vars };
  }
  if (run?.status === 'CANCELLED') {
    return { lane: 'NEEDS_TRANSPORT', reason: 'reason.seat.runCancelled', reasonVars: vars };
  }

  // ASSIGNED (or no boarding record yet).
  // Honest staleness: a seat pointing at a hotel this PNR no longer holds.
  if (!needsTransport(row)) {
    return { lane: 'NEEDS_TRANSPORT', reason: 'reason.seat.stale' };
  }
  if (run?.status === 'DEPARTED') {
    return { lane: 'NEEDS_TRANSPORT', reason: 'reason.seat.departedNoBoarding', reasonVars: vars };
  }
  if (run && RUN_NOT_DEPARTED.has(run.status)) {
    if (isReturn && RUN_NOT_DUE.has(run.status)) {
      return { lane: 'SCHEDULED', reason: 'reason.seat.returnScheduled', reasonVars: vars };
    }
    return {
      lane: 'WAITING',
      reason: isReturn ? 'reason.seat.returnBoarding' : 'reason.seat.waitingDeparture',
      reasonVars: vars,
    };
  }
  // No run in view (board unread / run pushed elsewhere): trust the seat's status.
  if (seat.status === 'IN_TRANSIT') {
    return { lane: 'EN_ROUTE', reason: 'reason.seat.inTransit', reasonVars: vars };
  }
  if (seat.status === 'FAILED' || seat.status === 'FAILED_COMPENSATED') return null; // let the car/demand path speak
  if (LIVE_TRIP.has(seat.status)) {
    return { lane: 'WAITING', reason: 'reason.seat.runNotInView', reasonVars: vars };
  }
  return null;
}

/** The roster's compact CAR fact when the trips read had nothing for this group. */
function rosterCarVerdict(row: DisruptionPnrGroupRow): Verdict | null {
  const t = row.transfer!;
  const isReturn = t.leg === 'HOTEL_TO_AIRPORT';
  switch (t.status) {
    case 'IN_TRANSIT':
      return { lane: 'EN_ROUTE', reason: 'reason.car.inTransit' };
    case 'DISPATCHED':
      return { lane: 'WAITING', reason: 'reason.car.driverAssigned' };
    case 'REQUESTED':
      return { lane: 'WAITING', reason: 'reason.car.requested' };
    case 'COMPLETED':
      return {
        lane: 'ARRIVED',
        reason: isReturn
          ? 'reason.car.completedReturn'
          : t.leg === 'AIRPORT_TO_HOME'
            ? 'reason.car.completedHome'
            : 'reason.car.completedOutbound',
      };
    case 'FAILED':
    case 'FAILED_COMPENSATED':
      return needsTransport(row)
        ? { lane: 'NEEDS_TRANSPORT', reason: 'reason.car.failed' }
        : null;
    default:
      return null; // CANCELLED / unknown — fall through to the demand.
  }
}

const EMPTY = (): { pnrs: number; pax: number } => ({ pnrs: 0, pax: 0 });

export function buildTransportDesk(
  rows: readonly DisruptionPnrGroupRow[],
  board: ITransferBoard | null | undefined,
  trips: readonly DisruptionTripView[] | undefined,
): TransportDeskState {
  const deskRows: TransportDeskRow[] = rows.map((row) => {
    const { mine } = splitTripsForGroup(trips ?? [], {
      subCaseUrn: row.subCaseUrn,
      pnrLabel: row.pnrLabel,
      pnrUrn: row.pnrUrn,
      paxUrns: row.passengers.map((p) => p.paxUrn),
    });
    const verdict = classifyPnr(row, mine as DisruptionTripView[], board);
    return { row, ...verdict };
  });

  const counts: TransportDeskState['counts'] = {
    ARRIVED: EMPTY(),
    EN_ROUTE: EMPTY(),
    WAITING: EMPTY(),
    SCHEDULED: EMPTY(),
    NEEDS_TRANSPORT: EMPTY(),
    OWN_MEANS: EMPTY(),
    NOT_NEEDED: EMPTY(),
  };
  for (const d of deskRows) {
    counts[d.lane].pnrs += 1;
    counts[d.lane].pax += d.row.paxCount ?? 0;
  }

  const queue = deskRows
    .filter((d) => d.lane === 'NEEDS_TRANSPORT')
    .sort((a, b) => (b.row.paxCount ?? 0) - (a.row.paxCount ?? 0) || a.row.pnrLabel.localeCompare(b.row.pnrLabel));

  const goalPnrs = deskRows.length - counts.NOT_NEEDED.pnrs;
  const goalPax = deskRows.reduce((s, d) => s + (d.row.paxCount ?? 0), 0) - counts.NOT_NEEDED.pax;

  // Issue #662: Derive the ground airport from the rows (overnightStation preferred, origin fallback).
  // Most rows in a disruption share the same overnight station; we pick the most common one.
  const airportCounts = new Map<string, number>();
  for (const d of deskRows) {
    const airport = d.row.overnightStation || d.row.origin;
    if (airport) airportCounts.set(airport, (airportCounts.get(airport) ?? 0) + 1);
  }
  let groundAirportIata: string | null = null;
  let maxCount = 0;
  for (const [airport, count] of airportCounts) {
    if (count > maxCount) {
      maxCount = count;
      groundAirportIata = airport;
    }
  }

  return {
    rows: deskRows,
    queue,
    counts,
    goalPnrs,
    goalPax,
    // An empty universe must NEVER read as victory — a cold roster / upstream
    // blip is "nothing loaded", not "everyone moved".
    allSettled: goalPnrs > 0 && counts.NEEDS_TRANSPORT.pnrs === 0,
    groundAirportIata,
  };
}

/** The row's "why am I here" i18n key (already computed with the lane). */
export function laneReason(d: TransportDeskRow): string {
  return d.reason;
}

/** Reason keys that mean "someone was left behind / the vendor died" — the
 *  exceptions queue's rose lane. Kept next to the classifier so a new failure
 *  reason can never quietly stop reading as one. */
export const ROSE_REASON_KEYS: ReadonlySet<string> = new Set([
  'reason.car.onDemandFailed',
  'reason.car.failedWithError',
  'reason.car.failed',
  'reason.seat.runFailed',
]);
